import React from 'react';
import { withStyles } from 'tss-react/mui';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import WaveTextField from '@common/components/form/WaveTextField';
import { DefaultCheckbox } from '@common/components/default-checkbox';
import MetadataService from '@common/services/MetadataService';

const styles = (theme) => ({
  container: {
    padding: theme.typography.pxToRem(25),
    paddingTop: theme.typography.pxToRem(22)
  },
  header: {
    paddingBottom: theme.typography.pxToRem(12)
  },
  registrationCutoff: {
    paddingBottom: theme.typography.pxToRem(15)
  },
  formLeft: {
    paddingRight: theme.typography.pxToRem(10)
  },
  formRight: {
    paddingLeft: theme.typography.pxToRem(10)
  },
  subsectionSpacing: {
    paddingBottom: theme.typography.pxToRem(15)
  },
  tooltipContainer: {
    backgroundColor: theme.palette.common.white,
    borderRadius: '2px',
    boxShadow: '0 2px 10px 0 #00000028',
    padding: theme.typography.pxToRem(20),
    opacity: 1
  },
  tooltipText: {
    color: '#0c0606de',
    fontSize: theme.typography.pxToRem(12),
    lineHeight: 1.5
  },
  popper: {
    opacity: 1
  }
});

const RegistrationCutoff = ({ classes }) => (
  <Typography className={classes.tooltipText}>
    Default number of days before a program start date that an educator cannot register for a program. Can be overridden at the program level.
  </Typography>
);

class Programs extends React.Component {
  meta = new MetadataService();

  handleInputChange = (event) => {
    const target = event.target;
    const value = target.value;
    const name = target.name;

    let institute = { ...this.props.instituteDetails };
    institute[name] = value;

    this.props.onChange(institute);
  };

  errorHelpers = (field) => {
    if (this.props.validationErrors.hasOwnProperty(field)) {
      return this.props.validationErrors[field][0];
    }
    return null;
  };

  errorStatus = (field) => {
    return this.props.validationErrors.hasOwnProperty(field);
  };

  makeFieldProps = (field) => {
    return {
      error: this.errorStatus(field),
      helperText: this.errorHelpers(field),
      name: field,
      onChange: this.handleInputChange,
      value: this.props.instituteDetails[field]
    };
  };

  render() {
    const { classes } = this.props;
    return (
      <form noValidate autoComplete="off">
        <Grid container spacing={2}>
          <Grid size={12}>
            <Paper className={classes.container}>
              <Typography className={classes.header} variant="h6">
                Dashboard
              </Typography>
              <Grid container spacing={4}>
                <Grid size={{xs:12, md:6}}>
                  <WaveTextField
                    label="Warning Days"
                    {...this.makeFieldProps('registration_warning_days')}
                  />
                </Grid>
                <Grid size={{xs:12, md:6}}>
                  <WaveTextField
                    label="Overdue Days"
                    {...this.makeFieldProps('registration_overdue_days')}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          <Grid size={12}>
            <Paper className={classes.container}>
              <Typography className={classes.header} variant="h6">
                Default Registration Cutoff
              </Typography>
              <Grid container>
                <Grid className={classes.registrationCutoff} size={6}>
                  <WaveTextField
                    label="Default Registration Cutoff (days)"
                    tooltipProps={{
                      placement: 'top',
                      titleComponent: <RegistrationCutoff classes={classes} />,
                      tooltipContainer: classes.tooltipContainer,
                      tooltipPopper: classes.popper
                    }}
                    {...this.makeFieldProps('registration_cutoff_days')}
                  />
                </Grid>
                <Grid className={classes.registrationCutoff} size={6}>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          <Grid size={12}>
            <Paper className={classes.container}>
              <Typography className={classes.header} variant="h6">
                Manual Approval
              </Typography>
              <Grid container>
                <Grid className={classes.registrationCutoff} size={6}>
                  <DefaultCheckbox
                    label="Professional Elective registrations require manual approval"
                    checkboxValue={true}
                    unCheckValue={false}
                    {...this.makeFieldProps(
                      'elective_registrations_require_approval'
                    )}
                  />
                </Grid>
                <Grid className={classes.registrationCutoff} size={6}>
                  <DefaultCheckbox
                    label="Professional Learning Journey registrations require manual approval"
                    checkboxValue={true}
                    unCheckValue={false}
                    {...this.makeFieldProps(
                      'learning_journey_registrations_require_approval'
                    )}
                  />
                </Grid>
                <Grid className={classes.registrationCutoff} size={6}>
                  <DefaultCheckbox
                    label="Vocational Elective registrations require manual approval"
                    checkboxValue={true}
                    unCheckValue={false}
                    {...this.makeFieldProps(
                      'vocational_elective_registrations_require_approval'
                    )}
                  />
                </Grid>
                <Grid className={classes.registrationCutoff} size={6}>
                  <DefaultCheckbox
                    label="Vocational Learning Journey registrations require manual approval"
                    checkboxValue={true}
                    unCheckValue={false}
                    {...this.makeFieldProps(
                      'vocational_learning_journey_registrations_require_approval'
                    )}
                  />
                </Grid>
                <Grid className={classes.registrationCutoff} size={12}>
                  <DefaultCheckbox
                    label="Workshop attendance required for program points"
                    checkboxValue={true}
                    unCheckValue={false}
                    {...this.makeFieldProps(
                      'program_points_require_workshop_attendance'
                    )}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          <Grid size={12}>
            <Paper className={classes.container}>
              <Typography className={classes.header} variant="h6">
                Default Contact Details
              </Typography>
              <Grid container>
                <Grid size={6} className={[classes.formLeft, classes.subsectionSpacing]}>
                  <WaveTextField
                    label="Default Contact Name"
                    {...this.makeFieldProps('program_default_contact_name')}
                  />
                </Grid>
                <Grid size={6} className={[classes.formRight, classes.subsectionSpacing]}>
                  <WaveTextField
                    label="Default Contact Job Title or Description"
                    {...this.makeFieldProps('program_default_contact_title')}
                  />
                </Grid>
                <Grid size={6} className={classes.formLeft}>
                  <WaveTextField
                    label="Default Contact Email"
                    type="email"
                    {...this.makeFieldProps('program_default_contact_email')}
                  />
                </Grid>
                <Grid size={6} className={classes.formRight}>
                  <WaveTextField
                    label="Default Contact Phone Number or Extension"
                    type="email"
                    {...this.makeFieldProps('program_default_contact_phone')}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </form>
    );
  }
}

export default withStyles(Programs, styles, {
  withTheme: true
});
